import { Capacitor } from "@capacitor/core";
import {
  CapacitorSQLite,
  type SQLiteConnection,
  type SQLiteDBConnection,
} from "@capacitor-community/sqlite";
import { getSqliteConn } from "./sqliteConn";

export interface EmployeeUser {
  _id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  isEmployee?: boolean;
  isApprover?: boolean;
  profile_pic?: string;
  department?: {
    _id: string;
    name: string;
  };
  employee_details?: {
    basic_details?: {
      onSite?: boolean;
    };
    biometric_authentication?: {
      face?: {
        photo?: string;
        Signature?: number[];
      };
    };
  };
  isPPEKitRequired?: boolean;
  employeeAttendance?: any[];
  [key: string]: any;
}

const DATABASE_NAME = "smcrm_users";
const isNative = Capacitor.isNativePlatform();

let sqliteConn: SQLiteConnection | null = null;
let sqliteDB: SQLiteDBConnection | null = null;

const ensureSQLiteReady = async () => {
  if (!isNative) return;

  if (sqliteDB) {
    try {
      if ((await sqliteDB.isDBOpen()).result) return;
    } catch (e) {
      // If isDBOpen fails, we fall through and re-initialize
      sqliteDB = null;
    }
  }

  try {
    if (!sqliteConn) {
      sqliteConn = getSqliteConn();
    }
    if (!sqliteConn) return;

    // Use consistency check now that sqliteConn is a singleton across the app
    try {
      await sqliteConn.checkConnectionsConsistency();
    } catch (e) {
      console.warn("checkConnectionsConsistency failed:", e);
    }

    let isConn = false;
    try {
      isConn = (await sqliteConn.isConnection(DATABASE_NAME, false)).result || false;
    } catch (e) {
      console.warn("[SQLite] isConnection threw error, assuming false:", e);
      isConn = false;
    }

    if (isConn) {
      try {
        sqliteDB = await sqliteConn.retrieveConnection(DATABASE_NAME, false);
      } catch (e) {
        console.warn("[SQLite] retrieveConnection failed, trying to close & create...", e);
        try {
          await CapacitorSQLite.closeConnection({ database: DATABASE_NAME, readonly: false });
        } catch(err){}
        sqliteDB = await sqliteConn.createConnection(DATABASE_NAME, false, "no-encryption", 1, false);
      }
    } else {
      try {
        sqliteDB = await sqliteConn.createConnection(DATABASE_NAME, false, "no-encryption", 1, false);
      } catch (e: any) {
        console.warn("[SQLite] createConnection failed, trying to retrieve...", e);
        sqliteDB = await sqliteConn.retrieveConnection(DATABASE_NAME, false);
      }
    }

    if (sqliteDB) {
      const dbOpen = await sqliteDB.isDBOpen();
      if (!dbOpen.result) {
        await sqliteDB.open();
      }
    }

    // 1. Create tables first
    await sqliteDB.execute(`
      CREATE TABLE IF NOT EXISTS employee_users (
        _id TEXT PRIMARY KEY NOT NULL,
        first_name TEXT,
        last_name TEXT,
        email TEXT,
        department_name TEXT,
        face_photo TEXT,
        face_signature TEXT,
        onSite INTEGER,
        isPPEKitRequired INTEGER,
        employeeAttendance TEXT,
        data TEXT
      );
      CREATE TABLE IF NOT EXISTS sync_metadata (key TEXT PRIMARY KEY NOT NULL, value TEXT);
    `);

    // 2. Add columns if missing (future proofing)
    const tableInfo = await sqliteDB.query("PRAGMA table_info(employee_users);");
    const existingCols = tableInfo.values?.map((c: any) => c.name) || [];

    const columnsToAdd = [
      { name: "first_name", type: "TEXT" },
      { name: "last_name", type: "TEXT" },
      { name: "email", type: "TEXT" },
      { name: "department_name", type: "TEXT" },
      { name: "face_photo", type: "TEXT" },
      { name: "face_signature", type: "TEXT" },
      { name: "onSite", type: "INTEGER" },
      { name: "isPPEKitRequired", type: "INTEGER" },
      { name: "employeeAttendance", type: "TEXT" },
      { name: "data", type: "TEXT" }
    ];

    for (const col of columnsToAdd) {
      if (!existingCols.includes(col.name)) {
        console.log(`[SQLite UserStorage] Adding missing column: ${col.name}`);
        await sqliteDB.execute(
          `ALTER TABLE employee_users ADD COLUMN ${col.name} ${col.type};`,
        );
      }
    }

    console.log(`[SQLite UserStorage] Schema successfully verified/updated.`);
  } catch (err) {
    console.error("SQLite Initialization Error", err);
  }
};

function prepareUserForDB(user: EmployeeUser) {
  const department_name = user.department?.name || "";
  const face_photo = user.employee_details?.biometric_authentication?.face?.photo || "";
  const face_signature = JSON.stringify(user.employee_details?.biometric_authentication?.face?.Signature || []);
  const onSite = user.employee_details?.basic_details?.onSite ? 1 : 0;
  const isPPEKitRequired = user.isPPEKitRequired ? 1 : 0;
  const employeeAttendance = JSON.stringify(user.employeeAttendance || []);

  return {
    _id: String(user._id),
    first_name: user.first_name || "",
    last_name: user.last_name || "",
    email: user.email || "",
    department_name,
    face_photo,
    face_signature,
    onSite,
    isPPEKitRequired,
    employeeAttendance,
    data: JSON.stringify(user),
  };
}

export const userStorage = {
  async ensureReady() {
    await ensureSQLiteReady();
  },

  async saveUser(user: EmployeeUser) {
    if (!isNative) return;
    await ensureSQLiteReady();
    if (!sqliteDB) return;

    const prepped = prepareUserForDB(user);
    const query = `INSERT OR REPLACE INTO employee_users (_id, first_name, last_name, email, department_name, face_photo, face_signature, onSite, isPPEKitRequired, employeeAttendance, data) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`;
    await sqliteDB.run(query, [
      prepped._id,
      prepped.first_name,
      prepped.last_name,
      prepped.email,
      prepped.department_name,
      prepped.face_photo,
      prepped.face_signature,
      prepped.onSite,
      prepped.isPPEKitRequired,
      prepped.employeeAttendance,
      prepped.data,
    ]);
  },

  async bulkSave(users: EmployeeUser[]) {
    if (!isNative || !users || users.length === 0) return;
    await ensureSQLiteReady();
    if (!sqliteDB) return;

    const sanitizedBatch = users
      .filter((u: any) => u._id)
      .map((u) => prepareUserForDB(u));

    const statement = `INSERT OR REPLACE INTO employee_users (_id, first_name, last_name, email, department_name, face_photo, face_signature, onSite, isPPEKitRequired, employeeAttendance, data) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`;

    const set = sanitizedBatch.map((u) => ({
      statement,
      values: [
        u._id,
        u.first_name,
        u.last_name,
        u.email,
        u.department_name,
        u.face_photo,
        u.face_signature,
        u.onSite,
        u.isPPEKitRequired,
        u.employeeAttendance,
        u.data,
      ],
    }));

    try {
      await sqliteDB.executeSet(set, true);
    } catch (e) {
      console.error("❌ SQLite Batch Insert Failed (Users):", e);
      throw e;
    }
  },

  async updatePPEStatus(updates: { userId: string; isPPEKitRequired: boolean }[]) {
    if (!isNative || !updates || updates.length === 0) return;
    await ensureSQLiteReady();
    if (!sqliteDB) return;

    for (const update of updates) {
      const user = await this.getUser(update.userId);
      if (user) {
        user.isPPEKitRequired = update.isPPEKitRequired;
        await this.saveUser(user);
      }
    }
  },

  async getUser(id: string): Promise<EmployeeUser | undefined> {
    if (!isNative) return undefined;
    await ensureSQLiteReady();
    if (!sqliteDB) return undefined;

    const res = await sqliteDB.query(
      `SELECT data FROM employee_users WHERE _id = ?`,
      [String(id)],
    );
    if (res?.values && res.values.length > 0) {
      return JSON.parse(res.values[0].data);
    }
    return undefined;
  },

  async getAllUsers(): Promise<EmployeeUser[]> {
    if (!isNative) return [];
    await ensureSQLiteReady();
    if (!sqliteDB) return [];

    const res = await sqliteDB.query(`SELECT data, face_signature FROM employee_users`);
    if (res?.values && res.values.length > 0) {
      return res.values.map((row: any) => {
        const user = JSON.parse(row.data);
        user.face_signature = row.face_signature;
        return user;
      });
    }
    return [];
  },

  async deleteUsersByIds(userIds: string[]) {
    if (!isNative || !userIds || userIds.length === 0) return;
    await ensureSQLiteReady();
    if (!sqliteDB) return;

    const stringIds = userIds.map(String);
    const chunkSize = 500;
    for (let i = 0; i < stringIds.length; i += chunkSize) {
      const chunk = stringIds.slice(i, i + chunkSize);
      const placeholders = chunk.map(() => "?").join(",");
      await sqliteDB.run(
        `DELETE FROM employee_users WHERE _id IN (${placeholders})`,
        chunk,
      );
    }
  },

  async clear() {
    if (!isNative) return;
    await ensureSQLiteReady();
    if (!sqliteDB) return;
    await sqliteDB.execute(`DELETE FROM employee_users`);
  },

  async saveSyncMetadata(dateStr: string) {
    if (!isNative) return;
    await ensureSQLiteReady();
    if (!sqliteDB) return;
    await sqliteDB.run(
      `INSERT OR REPLACE INTO sync_metadata (key, value) VALUES ('last_sync_date', ?)`,
      [dateStr],
    );
  },

  async getSyncMetadata(): Promise<string | null> {
    if (!isNative) return null;
    await ensureSQLiteReady();
    if (!sqliteDB) return null;
    const res = await sqliteDB.query(
      `SELECT value FROM sync_metadata WHERE key = 'last_sync_date'`,
    );
    if (res?.values && res.values.length > 0) {
      return res.values[0].value;
    }
    return null;
  },

  async saveLastPage(page: number) {
    if (!isNative) return;
    await ensureSQLiteReady();
    if (!sqliteDB) return;
    await sqliteDB.run(
      `INSERT OR REPLACE INTO sync_metadata (key, value) VALUES ('last_page_fetched', ?)`,
      [page.toString()],
    );
  },

  async getLastPage(): Promise<number> {
    if (!isNative) return 0;
    await ensureSQLiteReady();
    if (!sqliteDB) return 0;
    const res = await sqliteDB.query(
      `SELECT value FROM sync_metadata WHERE key = 'last_page_fetched'`,
    );
    if (res?.values && res.values.length > 0) {
      return parseInt(res.values[0].value || "0", 10);
    }
    return 0;
  },

  /**
   * Persist business-level settings (e.g. faceAccuracyThreshold) so the
   * attendance tablet can read them offline without hitting the server.
   */
  async saveBusinessSettings(settings: Record<string, any>) {
    await ensureSQLiteReady();
    const value = JSON.stringify(settings);
    if (isNative && sqliteDB) {
      await sqliteDB.run(
        `INSERT OR REPLACE INTO sync_metadata (key, value) VALUES ('business_settings', ?)`,
        [value],
      );
    } else {
      // Web fallback — use localStorage so the setting is available even on web
      try { localStorage.setItem("business_settings", value); } catch {}
    }
    // Let listeners (see helpers/appLifecycleScheduler) re-arm the auto start /
    // auto close schedule right away instead of waiting for the periodic re-arm.
    // NOTE: literal string on purpose — mirrors BUSINESS_SETTINGS_UPDATED_EVENT
    // without importing the scheduler (that would be a circular import).
    try {
      window.dispatchEvent(
        new CustomEvent("business-settings-updated", { detail: settings }),
      );
    } catch {}
  },

  /**
   * Retrieve persisted business settings.
   * Returns an empty object if nothing has been saved yet.
   */
  async getBusinessSettings(): Promise<Record<string, any>> {
    await ensureSQLiteReady();
    if (isNative && sqliteDB) {
      const res = await sqliteDB.query(
        `SELECT value FROM sync_metadata WHERE key = 'business_settings'`,
      );
      if (res?.values && res.values.length > 0) {
        try { return JSON.parse(res.values[0].value); } catch {}
      }
      return {};
    } else {
      // Web fallback
      try {
        const raw = localStorage.getItem("business_settings");
        if (raw) return JSON.parse(raw);
      } catch {}
      return {};
    }
  },

};
